import { IContextualMenuProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPSeparator, IPText, IPTooltipHost } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import EditTextFieldCallOut from 'components/CallOuts/EditTextFieldCallOut';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import RichTextReadOnly from 'components/Text/RichTextReadOnly';
import { deleteIcon, editIcon, globalStackTokensGapSmall, moreIcon } from 'globalStyles';
import { CommentTrailEntry } from 'models/comments';
import User from 'models/user';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateTimeMedium } from 'utils/datetime';

interface ISingleCommentProps {
  comment: CommentTrailEntry;
  isActionPending: boolean;
  onModify: (existingComment: CommentTrailEntry, updatedComment: string) => void;
  onRemove: (commentTrailId: number, commentTrailEntryId: number) => void;
  readOnly?: boolean;
}

const SingleComment = (props: ISingleCommentProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'commentTrail']);
  const [modify, setModify] = useState<boolean>(false);
  const [remove, setRemove] = useState<boolean>(false);
  const [value, setValue] = useState<string>(props.comment.comment);
  const targetId = 'uniqueTargetId' + Math.random().toString(36).substring(7);

  const onModify = (newValue: string) => {
    props.onModify(props.comment, newValue);
    setValue(newValue);
  };

  const getUserName = (id: string, defaultName: string | undefined): string => {
    const user: User = appContext.globalDataCache.users.get(id);
    if (user.email === '' && defaultName) {
      return defaultName;
    }

    return user.name;
  };

  const editMenuProps: IContextualMenuProps = {
    items: [
      {
        key: 'modify',
        text: t('commentTrail:Modify'),
        iconProps: editIcon,
        onClick: () => {
          setValue(props.comment.comment);
          setModify(true);
        },
      },
      {
        key: 'remove',
        iconProps: deleteIcon,
        text: t('commentTrail:Remove'),
        onClick: () => setRemove(true),
      },
    ],
    directionalHintFixed: true,
  };

  //
  // Main render
  //
  return (
    <IPStack>
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPersona
            imageUrl={appContext.globalDataCache.users.getPhotoURLObj(props.comment.createdById)}
            text={getUserName(props.comment.createdById, props.comment.createdBy)}
            hidePersonaDetails={true}
            size={IPPersonaSize.size24}
          />
        </IPStack.Item>
        <IPStack.Item grow>
          <IPStack>
            <IPStack horizontal tokens={globalStackTokensGapSmall}>
              <IPStack.Item>
                <IPText block variant="small">
                  <strong>{getUserName(props.comment.createdById, props.comment.createdBy)}</strong>
                </IPText>
              </IPStack.Item>
              <IPStack.Item>
                <IPText block nowrap variant="small">
                  {toLocaleDateTimeMedium(props.comment.created)}
                </IPText>
              </IPStack.Item>
              {props.comment.modifiedById && (
                <IPStack.Item>
                  <IPTooltipHost
                    content={t('commentTrail:EditedTooltip', {
                      user: getUserName(props.comment.modifiedById, props.comment.modifiedBy),
                      date: toLocaleDateTimeMedium(props.comment.modified),
                    })}
                  >
                    <IPText block variant="small">
                      <u>{t('commentTrail:Edited')}</u>
                    </IPText>
                  </IPTooltipHost>
                </IPStack.Item>
              )}
              {(!props.readOnly || appContext.user.id === props.comment.createdById) && (
                <IPStack.Item id={targetId}>
                  <IPIconButton
                    styles={{ root: { height: 16 } }}
                    iconProps={moreIcon}
                    menuProps={editMenuProps}
                    disabled={props.isActionPending}
                  ></IPIconButton>
                </IPStack.Item>
              )}
            </IPStack>
            <IPStack.Item>
              <RichTextReadOnly
                html={props.comment.comment}
                hideBorder={true}
                linkify={true}
                stripParagraph={true}
                maxHeight={-1}
              />
              {/* Mounted only while editing: its rich-text editor takes the focus when it starts, so a hidden one per
                  message pulled the focus out of the message editor below the trail. */}
              {modify && (
                <EditTextFieldCallOut
                  isBusy={props.isActionPending}
                  value={value}
                  isVisible={modify}
                  onClose={() => setModify(false)}
                  onUpdate={onModify}
                  targetId={targetId}
                  required={true}
                  maxLenght={4000}
                  width={700}
                  isHtml={true}
                />
              )}
              <DialogYesNo
                hidden={!remove}
                onYes={() => {
                  setRemove(false);
                  props.onRemove(props.comment.commentTrailId, props.comment.commentTrailEntryId);
                }}
                onNo={() => setRemove(false)}
                title={t('commentTrail:Dialogs.Remove.Title')}
                subText={t('commentTrail:Dialogs.Remove.SubText')}
              ></DialogYesNo>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
      <IPSeparator />
    </IPStack>
  );
};

export default SingleComment;
